import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";
import { LassieContext } from "../../context/LassieProvider";
import createSourceFromFeatures from "./createEllipseSourceFromFeatures";
import confidenceEllipse from "./confidenceEllipse";
import MapSettings from "../map-settings/MapSettings";
import MapReferences from "../map-references/MapReferences";
import MapPopup from "../map-popup/MapPopup";
import useOlMap from "./useOlMap";
import MapQa from "../map-qa/MapQa";
import { getIconForTurbine } from "./icons";
import { Point, Polygon } from "ol/geom";
import { Feature } from "ol";
import { fromLonLat } from "ol/proj";
import { Fill, Stroke, Style } from "ol/style";
import { Heatmap } from "ol/layer";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import "./ol-map.css";

const tileLayerFromUrl = (url) =>
  new TileLayer({
    source: new XYZ({
      url,
    }),
  });

export default function OlMap({
  turbines,
  lightning_strikes,
  forceShiftkeyToZoom = false,
  isHistoricLightningAnalysis = false,
}) {
  const { selectedTurbine, setSelectedTurbine, layerSettings, setLayerSettings } = useContext(LassieContext);
  const [popoutSettingsOpen, setPopoutSettingsOpen] = useState(false);
  const [popoutReferencesOpen, setPopoutReferencesOpen] = useState(false);
  const [qaOpen, setQaOpen] = useState({ status: false, type: "" });
  const [showLightningTable, setShowLightningTable] = useState(true);
  const [showSensorTable, setShowSensorTable] = useState(true);
  const [layers, setLayers] = useState({});

  const {
    initialized,
    map,
    mapElement,
    setInitialSourceRender,
    turbineStyling,
  } = useOlMap();

  // turbine selection handling
  const [highlighted, setHighlighted] = useState(null);
  const [hovering, setHovering] = useState(null);

  // display 'hold shift to zoom' popup
  const [showHoldShiftToZoomPopup, setShowHoldShiftToZoomPopup] =
    useState(false);

  useEffect(() => {
    if (showHoldShiftToZoomPopup) {
      setTimeout(() => {
        setShowHoldShiftToZoomPopup(false);
      }, 5000);
    }
  }, [showHoldShiftToZoomPopup]);

  useEffect(() => {
    if (!selectedTurbine || !layerSettings.only_show_outside_iec || !lightning_strikes) return;

    const turbinesWithOutsideIecLightning = new Set(
      lightning_strikes
        .filter(strike => strike.outside_certification)
        .map(strike => strike.turbine_id)
    );

    if (!turbinesWithOutsideIecLightning.has(selectedTurbine.id)) {
      setSelectedTurbine(null);
      if (highlighted) {
        highlighted.setStyle(turbineStyling(highlighted, map).normal);
        setHighlighted(null);
      }
    }
  }, [layerSettings.only_show_outside_iec, selectedTurbine, lightning_strikes, highlighted, turbineStyling, map]);

  const handleOnPointerMove = useCallback(
    (e) => {
      let newHover = null;
      map.forEachFeatureAtPixel(e.pixel, function (f) {
        if (!f.get("turbine_id")) return;
        if (newHover) return;
        newHover = f;
      });
      if (newHover === hovering) return;

      setHovering((previousHover) => {
        if (newHover) {
          if (
            !selectedTurbine ||
            newHover.get("turbine_id") !== selectedTurbine.id
          ) {
            newHover.setStyle(turbineStyling(newHover, map).hover);
          }
        }
        if (previousHover) {
          if (
            !selectedTurbine ||
            previousHover.get("turbine_id") !== selectedTurbine.id
          ) {
            previousHover.setStyle(turbineStyling(previousHover, map).normal);
          }
        }
        return newHover;
      });
    },
    [hovering, turbineStyling, selectedTurbine, map]
  );

  const handleOnClick = useCallback(
    (e) => {
      setPopoutSettingsOpen(false);
      setPopoutReferencesOpen(false);
      if (highlighted !== null) {
        highlighted.setStyle(turbineStyling(highlighted, map).normal);
        setHighlighted(null);
      }

      let newlySelected = null;
      map.forEachFeatureAtPixel(e.pixel, function (f) {
        if (highlighted === f) return;

        const turbineId = f.get("turbine_id");
        if (!turbineId) return;
        newlySelected = f;

        newlySelected.setStyle(turbineStyling(newlySelected, map).selected);
        setHighlighted(newlySelected);
        return true;
      });

      if (newlySelected) {
        const turbineId = newlySelected.get("turbine_id");
        if (!turbineId) return;
        setSelectedTurbine(turbines.find((t) => t.id === turbineId));
      } else {
        setSelectedTurbine(null);
      }
    },
    [highlighted, turbineStyling, map, turbines]
  );

  // if only one turbine, show lightning map
  useEffect(() => {
    if (!initialized) return;
    setLayerSettings((prev) => ({
      ...prev,
      lightning: turbines.length === 1,
    }));
  }, [turbines, initialized]);

  // set up and destroy mouse callbacks
  useEffect(() => {
    if (!initialized) return;

    const w = (evt) => {
      const { deltaY, ctrlKey } = evt.originalEvent;
      if (forceShiftkeyToZoom && !ctrlKey) {
        setShowHoldShiftToZoomPopup(true);
        evt.stopPropagation();
        return;
      }

      setShowHoldShiftToZoomPopup(false);

      if (deltaY !== 0) {
        evt.preventDefault();
      }
    };

    map.on("wheel", w);
    map.on("click", handleOnClick);
    map.on("pointermove", handleOnPointerMove);

    return () => {
      map.un("wheel", w);
      map.un("click", handleOnClick);
      map.un("pointermove", handleOnPointerMove);
    };
  }, [initialized, map, handleOnClick, handleOnPointerMove]);

  const lightningGroups = useMemo(() => {
    if (lightning_strikes === null) return null;

    const lightningGroups = {};
    const allLightningOut = [];
    const allLightningMostProb = [];
    const allLightningStrikes = new Map();

    // Endpoints that send a confidence_ellipse keep using theirs; the map's own endpoint does
    // not send one, so it is derived from the axes and angle on the strike. Either way each
    // ring is projected once and shared - a strike can appear under several turbines.
    const ellipseRings = new Map();
    const ellipseRing = (tl) => {
      let ring = ellipseRings.get(tl.lightning_id);
      if (!ring) {
        const lonLats = tl.confidence_ellipse
          ? tl.confidence_ellipse.map((c) => [c.lon, c.lat])
          : confidenceEllipse(tl);
        ring = lonLats.map((c) => fromLonLat(c, "EPSG:3857"));
        ellipseRings.set(tl.lightning_id, ring);
      }
      return ring;
    };

    let turbinesToProcess = turbines;
    if (layerSettings.only_show_outside_iec) {
      const turbinesWithOutsideIecLightning = new Set(
        lightning_strikes
          .filter(strike => strike.outside_certification)
          .map(strike => strike.turbine_id)
      );
      turbinesToProcess = turbines.filter(t =>
        turbinesWithOutsideIecLightning.has(t.id)
      );
    }

    turbinesToProcess.forEach((turbine) => {
      const allTurbineStrikes = [];
      const outsideIec = [];
      const turbineMostProbable = [];
      const turbineMostProbableOutsideIec = [];
      lightning_strikes
        .filter((tl) => tl.turbine_id === turbine.id)
        .map((tl) => {
          const f = {
            type: "Feature",
            properties: {
              lightning_id: tl.lightning_id,
              peak_current: tl.peak_current,
            },
            geometry: {
              type: "Polygon",
              coordinates: [ellipseRing(tl)],
            },
          };

          if (!allLightningStrikes.has(tl.lightning_id)) {
            allLightningStrikes.set(tl.lightning_id, f);
          }

          if (tl.most_probable) {
            if (tl.outside_certification) {
              allLightningOut.push(f);
            }
            allLightningMostProb.push(f);
            turbineMostProbable.push(f);

            if (tl.outside_certification) {
              turbineMostProbableOutsideIec.push(f);
            }
          }

          if (tl.outside_certification) {
            outsideIec.push(f);
          }
          // since this layer shows all lightning - we can use most_probable to only select one turbine_lightning for each lightning
          allTurbineStrikes.push(f);
        });

      lightningGroups[turbine.id] = {
        all: createSourceFromFeatures(allTurbineStrikes),
        outside: createSourceFromFeatures(outsideIec),
        most_probable: createSourceFromFeatures(turbineMostProbable),
        most_probable_outside_iec: createSourceFromFeatures(
          turbineMostProbableOutsideIec
        ),
      };
    });

    const uniqueAllLightningStrikes = Array.from(allLightningStrikes.values());

    lightningGroups["unified"] = {
      all: createSourceFromFeatures(uniqueAllLightningStrikes),
      outside: createSourceFromFeatures(allLightningOut),
      most_probable: createSourceFromFeatures(allLightningMostProb),
      most_probable_outside_iec: createSourceFromFeatures(allLightningOut),
    };

    return lightningGroups;
  }, [turbines, lightning_strikes, isHistoricLightningAnalysis, layerSettings.only_show_outside_iec]);

  const turbineSource = useMemo(() => {
    if (!turbines || turbines.length === 0) return null;

    let visibleTurbines = turbines;

    if (layerSettings.only_show_outside_iec && lightning_strikes) {
      visibleTurbines = turbines.filter(t => lightningGroups && lightningGroups[t.id]);
    }

    const turbineFeatures = visibleTurbines.map((t) => {
      const url = getIconForTurbine(t, layerSettings.most_probable, isHistoricLightningAnalysis);
       const f = new Feature({
        geometry: new Point(fromLonLat([t.lon, t.lat], "EPSG:3857")),
        name: t.name,
        turbine_id: t.id,
        status_icon: url,
        canHover: true,
      });
      f.setStyle(turbineStyling(f, map).normal);

      return f;
    });

    return new VectorSource({
      features: turbineFeatures,
    });
  }, [turbines, turbineStyling, layerSettings, map, lightning_strikes, isHistoricLightningAnalysis, lightningGroups]);

  const collectionAreaSource = useMemo(() => {
    if (!turbines || turbines.length === 0) return null;

    let visibleTurbines = turbines;

    if (layerSettings.only_show_outside_iec && lightning_strikes) {
      visibleTurbines = turbines.filter(t => lightningGroups && lightningGroups[t.id]);
    }

    const collectionAreaFeatures = visibleTurbines.map((t) => {
      const f = new Feature({
        geometry: new Polygon([
          t.collection_area.map((c) => fromLonLat([c.lon, c.lat], "EPSG:3857")),
        ]),
      });
      f.setStyle(
        new Style({
          fill: new Fill({
            color: "rgba(115,142,218, 1)", // opacity required on layer for dissolve
          }),
        })
      );
      return f;
    });
    return new VectorSource({
      features: collectionAreaFeatures,
    });
  }, [turbines, turbineStyling, layerSettings, map, lightning_strikes, lightningGroups]);


  // set source for first render (turbineSource)
  useEffect(() => {
    if (!turbineSource) return;
    setInitialSourceRender(turbineSource);
  }, [turbineSource]);

  useEffect(() => {
    const res = {};
    turbines.forEach((turbine) => {
      res[turbine.id] = new VectorLayer({
        source: createSourceFromFeatures([
          {
            type: "Feature",
            properties: {},
            geometry: {
              type: "Polygon",
              coordinates: [
                turbine.collection_area.map((c) =>
                  fromLonLat([c.lon, c.lat], "EPSG:3857")
                ),
              ],
            },
          },
        ]),
        style: [
          new Style({
            stroke: new Stroke({
              color: "black",
              width: 1,
            }),
            fill: new Fill({
              color: "rgba(115,142,218, 0.3)",
            }),
          }),
        ],
      });
    });
    setLayers((prev) => ({
      ...prev,
      turbine_collection_area: res,
    }));
  }, [turbines]);

  // update lightning layers if lightning groups change
  useEffect(() => {
    if (!lightningGroups) return;

    const turbineLightningLayers = {};
    Object.keys(lightningGroups).forEach((turbine_id) => {
      const {
        outside,
        all,
        most_probable,
        most_probable_outside_iec,
        collection_area,
      } = lightningGroups[turbine_id];
      if (layerSettings.only_show_outside_iec) {
        // if (outside.length === 0) return;
      }

      let relevant = null;
      if (layerSettings.most_probable) {
        if (layerSettings.only_show_outside_iec) {
          relevant = most_probable_outside_iec;
        } else {
          relevant = most_probable;
        }
      } else {
        if (layerSettings.only_show_outside_iec) {
          relevant = outside;
        } else {
          relevant = all;
        }
      }

      turbineLightningLayers[turbine_id] = new VectorLayer({
        source: relevant,
        style: [
          new Style({
            stroke: new Stroke({
              color: "black",
              width: 1,
            }),
            fill: new Fill({
              color: "rgba(255, 0, 0, 0.2)",
            }),
          }),
        ],
      });
    });
    setLayers((prev) => ({
      ...prev,
      lightning: turbineLightningLayers,
    }));
  }, [lightningGroups, layerSettings]);

  const heatmapLayerSource = useMemo(() => {
    if (!lightning_strikes) return null;

    const lightningFeatures = lightning_strikes
      .filter((t) => t.most_probable)
      .map(
        (t) =>
          new Feature({
            geometry: new Point(fromLonLat([t.lon, t.lat], "EPSG:3857")),
            semi_major_axis: t.semi_major_axis,
          })
      );

    return new VectorSource({
      features: lightningFeatures,
    });
  }, [lightning_strikes]);

  // set layers
  useEffect(() => {
    if (!heatmapLayerSource || !turbineSource) return;

    const initTurbineLayer = new VectorLayer({
      source: turbineSource,
    });

    const heatmapLayer = new Heatmap({
      source: heatmapLayerSource,
      blur: 25,
      radius: 15,
      weight: function (feature) {
        const semiMajor = parseFloat(feature.get("semi_major_axis"));
        const normalizationFactor = 0.4
        return 1 / (1 + semiMajor / normalizationFactor);
      },

      gradient: ['#000004', '#2c115f', '#721f81', '#b73779', '#f1605d', '#feb078', '#fcfdbf']
    });

    setLayers((prev) => ({
      ...prev,
      turbines: initTurbineLayer,
      heatmap: heatmapLayer,
      backgroundImage: tileLayerFromUrl(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
      ),
      backgroundTopo: tileLayerFromUrl(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
      ),
      backgroundElevation: tileLayerFromUrl(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}'
      ),
      collection_area: new VectorLayer({
        source: collectionAreaSource,
        opacity: 0.3,
      }),
    }));
  }, [heatmapLayerSource, turbineSource, collectionAreaSource]);

  // Update turbine colors based on selected turbine legend

  useEffect(() => {
    if (!initialized) return;
    const layers = map.getLayers();
    if (!layers) return;
    const newTurbineLayer = new VectorLayer({
      source: turbineSource,
    });
    setLayers((prev) => ({ ...prev, turbines: newTurbineLayer }));
  }, [map, initialized, turbineSource]);

  // Render layers from settings

  useEffect(() => {
    if (!initialized || !map || !layers || !layers.lightning) return;

    const selectedLayers = [];

    if (layerSettings.background === "topographic" && layers.backgroundTopo) {
      selectedLayers.push(layers.backgroundTopo);
    } else if (layerSettings.background === "elevation" && layers.backgroundElevation) {
      selectedLayers.push(layers.backgroundElevation);
    } else if (layers.backgroundImage) {
      selectedLayers.push(layers.backgroundImage);
    }

    if (layerSettings.collection_area && layers.collection_area) {
      selectedLayers.push(layers.collection_area);
    }

    if (selectedTurbine) {
      const turbineLayer = layers.turbine_collection_area?.[selectedTurbine.id];
      const lightningLayer = layers.lightning?.[selectedTurbine.id];

      if (turbineLayer) selectedLayers.push(turbineLayer);
      if (lightningLayer) selectedLayers.push(lightningLayer);
    } else {
      if (layerSettings.lightning && layers.lightning.unified) {
        selectedLayers.push(layers.lightning.unified);
      }
    }

    if (layerSettings.heatmap && layers.heatmap) {
      selectedLayers.push(layers.heatmap);
    }

    if (layerSettings.turbines && layers.turbines) {
      selectedLayers.push(layers.turbines);
    }

    map.setLayers(selectedLayers);
  }, [map, layerSettings, initialized, layers, selectedTurbine]);

  return (
    <div className="ol-map-parent">
      {qaOpen.status && qaOpen.type !== "" && (
        <MapQa qaOpen={qaOpen} setQaOpen={setQaOpen} />
      )}

      <div
        ref={mapElement}
        key="main-wf-map"
        className="ol-map-container"
      ></div>

      {selectedTurbine && !isHistoricLightningAnalysis && <MapPopup
        layerSettings={layerSettings}
        showLightningTable={showLightningTable}
        setShowLightningTable={setShowLightningTable}
        showSensorTable={showSensorTable}
        setShowSensorTable={setShowSensorTable}
      />}

      {initialized && !isHistoricLightningAnalysis && (
        <MapReferences
          popoutReferencesOpen={popoutReferencesOpen}
          setPopoutReferencesOpen={setPopoutReferencesOpen}
          setQaOpen={setQaOpen}
          setPopoutSettingsOpen={setPopoutSettingsOpen}
          popoutSettingsOpen={popoutSettingsOpen}
        />
      )}

      {initialized && (
        <MapSettings
          popoutSettingsOpen={popoutSettingsOpen}
          setPopoutSettingsOpen={setPopoutSettingsOpen}
          setQaOpen={setQaOpen}
          setPopoutReferencesOpen={setPopoutReferencesOpen}
          popoutReferencesOpen={popoutReferencesOpen}
          isHistoricLightningAnalysis={isHistoricLightningAnalysis}
        />
      )}

      {showHoldShiftToZoomPopup && (
        <div className="hold-shift-to-zoom-popup">
          <p>Hold control to zoom</p>
        </div>
      )}
    </div>
  );
}
